import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Truck, Package, Boxes, Layers, ShieldCheck, Route } from "lucide-react";
import { IMG } from "@/lib/site";
import { Reveal, Eyebrow } from "@/components/Reveal";

const FLEET = [
  { icon: Truck, t: "Transporter", d: "Wendig in der Stadt – ideal für Möbel, Kartons und Einzelstücke." },
  { icon: Truck, t: "LKW & Möbelkoffer", d: "Großes Ladevolumen für komplette Haushalte und Firmenumzüge." },
  { icon: Layers, t: "Paletten & Stückgut", d: "Gewickelt, gesichert und sauber verladen – auch ab Rampe." },
  { icon: Boxes, t: "Möbel & Sperrgut", d: "Gepolstert, gegurtet und so verladen, dass nichts verrutscht." },
  { icon: Package, t: "Kartons & Material", d: "Umzugskartons, Folie, Luftpolster und Decken auf Wunsch." },
  { icon: Route, t: "Transportwege", d: "Route, Zeitfenster und Zugänge werden vorher geklärt." },
];

export const Logistics = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="logistik"
      ref={ref}
      data-testid="logistics-section"
      className="relative w-full bg-[#0a0a0a] grain overflow-hidden py-20 sm:py-24 lg:py-32"
    >
      <div className="w-full px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow chapter="02">Transport & Logistik</Eyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display uppercase font-black leading-[0.9] tracking-tight text-white text-[clamp(2.1rem,7vw,4.4rem)]">
                Transport ist<br />
                <span className="text-[#f95700]">unser Kerngeschäft.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-[15px] sm:text-base text-neutral-400 leading-relaxed max-w-xl">
                Wir bewegen Möbel, Kartons, Paletten und gewerbliche Waren – als Einzelfahrt, als
                Beiladung oder als kompletter Umzug. Fahrzeug, Teamgröße und Route richten sich
                nach dem, was tatsächlich transportiert wird. Ladung wird fachgerecht gesichert,
                Zugänge und Etagen vorab geklärt.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-8 flex items-center gap-4 border border-white/15 p-5">
                <ShieldCheck className="text-[#f95700] shrink-0" size={28} />
                <p className="text-sm text-neutral-300">
                  Decken, Gurte, Kantenschutz und Folie gehören bei uns zur Standardausrüstung –
                  nicht zum Aufpreis-Extra.
                </p>
              </div>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-px bg-white/10">
              {FLEET.map((f, i) => (
                <Reveal key={f.t} delay={0.05 * i} className="bg-[#141414] p-5 sm:p-6 group">
                  <f.icon size={22} className="text-[#f95700] mb-4 transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="font-display text-lg font-bold uppercase text-white">{f.t}</h3>
                  <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">{f.d}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-4 sm:gap-5">
            <Reveal className="relative overflow-hidden">
              <motion.img
                style={{ y }}
                src={IMG.loadingDark}
                alt="Beladener Transporter mit gesicherten Möbeln"
                className="w-full h-[42vh] sm:h-[50vh] lg:h-[58vh] object-cover scale-110"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              <div className="absolute bottom-0 left-0 bg-black/80 backdrop-blur px-5 sm:px-6 py-3.5 sm:py-4">
                <div className="eyebrow text-[#f95700]">Ladungssicherung</div>
                <div className="font-display text-xl sm:text-2xl font-bold uppercase text-white mt-1">
                  Gepolstert & gegurtet
                </div>
              </div>
            </Reveal>

            <div className="grid grid-cols-3 gap-3 sm:gap-5">
              {[
                { src: IMG.palletsDark, cap: "Paletten" },
                { src: IMG.boxes, cap: "Kartons" },
                { src: IMG.truckAutobahn, cap: "Fernstrecke" },
              ].map((im, i) => (
                <Reveal key={im.cap} delay={0.08 * i} className="relative overflow-hidden group">
                  <img
                    src={im.src}
                    alt={im.cap}
                    loading="lazy"
                    className="w-full h-28 sm:h-44 lg:h-52 object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                  />
                  <span className="absolute bottom-2 left-2 font-mono-t text-[9px] sm:text-[10px] uppercase tracking-widest bg-black/75 text-white px-2 py-1">
                    {im.cap}
                  </span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
